import DialogContentText from "@material-ui/core/DialogContentText";
import { makeStyles, Theme } from "@material-ui/core/styles";
import ActionDialog from "@saleor/components/ActionDialog";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import {
  // DEFAULT_INITIAL_PAGINATION_DATA,
  DEFAULT_INITIAL_SEARCH_DATA
  // defaultListSettings
} from "@saleor/config";
import { db } from "@saleor/db";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useDownloadReport from "@saleor/hooks/useDownloadReport";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useUser from "@saleor/hooks/useUser";
import { maybe } from "@saleor/misc";
import useBrandSearch from "@saleor/searches/useBrandSearch";
import usePayoutMallsSearch from "@saleor/searches/usePayoutMallsSearch";
import { ListViews } from "@saleor/types";
import {
  PayoutBatchTaskStatus,
  SettlementStatusEnum
} from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import Moment from "moment-timezone";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import FinanceOrderListPage from "../../components/FinanceOrderListPage";
import GenerateBatchDialog from "../../components/GenerateBatchDialog";
import {
  financeOrderListUrl,
  FinanceOrderListUrlQueryParams
} from "../../urls";
import { InvalidThreeMonth } from "../../utils/handlers";
import {
  getReportStructure,
  selectReportData
} from "../../utils/reportsDownload";
import FinanceAdjustDialog, {
  AdjustDialogForm
} from "./../../components/FinanceAdjustDialog";
import {
  useBulkUpdateSettlementMutation,
  useGeneratePayoutBatchMutation,
  useUpdateOpenOrdersMutation
} from "./../../mutation";
import {
  useInitialFinanceOrderFilterDataQuery,
  usePayoutBatchTaskQuery,
  usePayoutFinanceAdjustment,
  usePayoutMallsQuery,
  usePayoutOrderList,
  usePayoutOrderListDownloadQuery
} from "./../../queries";
import {
  areFiltersApplied,
  getFilterOpts,
  getFilterQueryParam,
  getFilterVariables
  // getFilterTabs
} from "./filters";
import { handleBulkUpdate } from "./handlers";
import createToolbar from "./toolbar";

interface FinanceOrderListProps {
  params: FinanceOrderListUrlQueryParams;
}

interface MakeStylesProps {
  disabled: boolean;
}

const useStyles = makeStyles<Theme, MakeStylesProps>(
  theme => ({
    btnGenerateBatch: ({ disabled }) => ({
      backgroundColor: "#4caf50",
      ...{
        "& span label": {
          color: theme.palette.primary.contrastText,
          cursor: "pointer"
        },
        "&:hover": {
          backgroundColor: "#388e3c"
        }
      },
      ...(disabled && {
        backgroundColor: theme.palette.action.disabledBackground,
        ...{
          "& span label": {
            color: theme.palette.action.disabled
          }
        }
      }),
      marginLeft: "10px"
    }),
    whiteColorLabel: {
      color: "#fff"
    }
  }),
  {
    name: "FinanceOrderListProps"
  }
);

const FinanceOrderList: React.FC<FinanceOrderListProps> = ({ params }) => {
  const dateNow = Moment(new Date()).format("YYYYMMDD");
  const navigate = useNavigator();
  const notify = useNotifier();
  const paginate = usePaginator();
  const currencySymbol = "PHP";
  const intl = useIntl();
  const [batchTask, setBatchTask] = useState("");
  const [downloadStatus, setDownloadStatus] = useState<
    ConfirmButtonTransitionState
  >("default");
  const { user } = useUser();

  const [loadData, setLoadData] = useState(false);
  const [expressPaymentData, setExpressPaymentData] = useState(false);
  const [forExpressPayment, setForExpressPayment] = useState([]);
  const [downloadPaginationData, setDownloadPaginationData] = useState("");

  const { updateListSettings, settings } = useListSettings(
    ListViews.REPORTS_LIST
  );

  const [openModal, closeModal] = createDialogActionHandlers(
    navigate,
    financeOrderListUrl,
    params
  );

  const paginationState = createPaginationState(settings.rowNumber, params);
  const {
    isSelected,
    listElements,
    toggle,
    toggleAll,
    reset: resetSelected
  } = useBulkActions(params.ids);

  const reportStructure = getReportStructure(
    user?.userPermissions,
    params.payoutStatus
  );

  const previewVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params)
    }),
    [params]
  );

  const reportDownloadVariables = React.useMemo(
    () => ({
      after: downloadPaginationData,
      filter: getFilterVariables(params),
      first: 1000
    }),
    [params]
  );

  const { data: initialFilterData } = useInitialFinanceOrderFilterDataQuery({
    variables: {
      brand: params.brand
    }
  });

  const { data: payoutMalls } = usePayoutMallsQuery({
    displayLoader: false,
    variables: {
      filter: {
        mallId: params.mall,
        settlementStatus: params.payoutStatus
      }
    }
  });

  const searchBrand = useBrandSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      first: 5
    }
  });

  const searchPayoutMall = usePayoutMallsSearch({
    variables: {
      first: 5,
      ...DEFAULT_INITIAL_SEARCH_DATA,
      settlementStatus: params.payoutStatus
    }
  });

  const filterOpts = getFilterOpts(
    {
      initial: maybe(
        () => initialFilterData.brands.edges.map(edge => edge.node) || []
      ),
      search: searchBrand
    },
    {
      initial: maybe(
        () => payoutMalls.payoutMalls.edges.map(edge => edge.node) || []
      ),
      search: searchPayoutMall
    },
    params
  );

  React.useEffect(() => {
    refetchPayoutList();
  }, [settings.rowNumber]);

  const [changeFilters, resetFilters] = createFilterHandlers({
    createUrl: financeOrderListUrl,
    getFilterQueryParam,
    navigate,
    params
  });

  const {
    data: PayoutOrders,
    loading,
    refetch: refetchPayoutList
  } = usePayoutOrderList({
    displayLoader: true,
    skip: !Object.keys(params).includes("mall"),
    variables: previewVariables
  });

  const generateBatchDisabled =
    (params.mall && PayoutOrders?.storeOrdersPayout?.totalCount <= 0) ||
    PayoutOrders?.storeOrdersPayout?.totalCount === undefined;

  const classes = useStyles({
    disabled: generateBatchDisabled
  });

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => PayoutOrders?.storeOrdersPayout?.pageInfo),
    paginationState,
    params
  );

  const {
    data: downloadQueryData,
    loading: loadingDownloadQueryData
  } = usePayoutOrderListDownloadQuery({
    displayLoader: true,
    skip: !loadData || !Object.keys(params).includes("mall"),
    variables: reportDownloadVariables
  });

  const financeOrdersData = downloadQueryData?.storeOrdersPayout?.edges?.map(
    edge => ({ ...edge.node, download_date: dateNow })
  );

  const downloadDataPageInfo = downloadQueryData?.storeOrdersPayout?.pageInfo;

  const {
    data: expressPaymentQueryData,
    loading: loadingExpressPaymentQueryData
  } = usePayoutOrderListDownloadQuery({
    displayLoader: true,
    skip: !expressPaymentData || !Object.keys(params).includes("mall"),
    variables: reportDownloadVariables
  });

  const forExpressPaymentIds = expressPaymentQueryData?.storeOrdersPayout?.edges?.map(
    edge => edge.node.id
  );

  const expressPaymentDataPageInfo =
    expressPaymentQueryData?.storeOrdersPayout?.pageInfo;

  const tabs = [
    ...[
      {
        data: {
          payoutStatus: SettlementStatusEnum.OPEN
        },
        name: "Open Orders"
      },
      {
        data: {
          payoutStatus: SettlementStatusEnum.FOR_PAYMENT
        },
        name: "For Payment"
      },
      {
        data: {
          payoutStatus: SettlementStatusEnum.EXCLUDED
        },
        name: "Excluded"
      }
    ]
  ];

  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length
        : 0
      : parseInt(params.activeTab, 0);

  const handleTabChange = (tab: number) => {
    resetSelected();
    navigate(
      financeOrderListUrl({
        ...params,
        activeTab: tab.toString(),
        ...tabs[tab].data
      })
    );

    // if (currentTab === 0) {
    //   navigate(
    //     financeOrderListUrl({
    //       activeTab: (tab - 1).toString(),
    //       ...tabs[tab - 1].data
    //     })
    //   );
    // } else {
    //   navigate(
    //     financeOrderListUrl({
    //       activeTab: tab.toString(),
    //       ...tabs[0].data
    //     })
    //   );
    // }
  };

  const handleSearch = (text: string) => {
    if (text) {
      params.after = null;
      params.before = null;
    }
    navigate(financeOrderListUrl({ ...params, query: text }));
  };

  const handleDownloadDialog = () => {
    if (params.mall && PayoutOrders?.storeOrdersPayout?.totalCount > 0) {
      openModal("download");
    }
  };

  const handleExpressPaymentDialog = () => {
    if (params.mall && PayoutOrders?.storeOrdersPayout?.totalCount > 0) {
      openModal("for-payment-express");
    }
  };

  const handleCancelDownload = () => {
    setDownloadStatus("default");
    setLoadData(false);
    closeModal();
  };

  const handleConfirmDownload = () => {
    setLoadData(true);
    setDownloadStatus("loading");
  };

  const handleConfirmExpressPayment = () => {
    setExpressPaymentData(true);
  };

  // Download report
  React.useEffect(() => {
    if (!loadData || loadingDownloadQueryData) {
      return;
    }

    // Add finance order data to indexeddb
    db.payoutOrders.bulkPut(financeOrdersData);
    setDownloadPaginationData(downloadDataPageInfo.endCursor);

    if (
      !downloadDataPageInfo.hasNextPage &&
      downloadDataPageInfo?.endCursor === null
    ) {
      db.payoutOrders
        .where("download_date")
        .equals(dateNow)
        .toArray()
        .then(data => {
          const selectedReportData = selectReportData(data, reportStructure);

          useDownloadReport(
            selectedReportData,
            reportStructure.fields,
            null,
            reportStructure.filename,
            "csv"
          );

          setDownloadStatus("success");
          setDownloadPaginationData("");
          setLoadData(false);

          notify({
            status: "success",
            text: intl.formatMessage({
              defaultMessage: "Store Orders successfully downloaded."
            })
          });
          // Clear indexeddb data
          db.payoutOrders
            .where("download_date")
            .equals(dateNow)
            .delete();

          closeModal();
        });
    }
  }, [loadData, loadingDownloadQueryData]);

  React.useEffect(() => {
    if (!expressPaymentData || loadingExpressPaymentQueryData) {
      return;
    }

    setForExpressPayment(current => [...current, forExpressPaymentIds]);
    setDownloadPaginationData(expressPaymentDataPageInfo.endCursor);

    if (
      !expressPaymentDataPageInfo.hasNextPage &&
      expressPaymentDataPageInfo?.endCursor === null
    ) {
      handleBulkUpdate(
        SettlementStatusEnum.FOR_PAYMENT,
        forExpressPayment.flat(),
        BulkUpdateSettlementStatus
      );
      setDownloadPaginationData("");
      setExpressPaymentData(false);
      setForExpressPayment([]);
    }
  }, [expressPaymentData, loadingExpressPaymentQueryData]);

  React.useEffect(() => {
    if (
      InvalidThreeMonth(params.createdFrom, params.createdTo) ||
      InvalidThreeMonth(
        params.orderCompleteDateFrom,
        params.orderCompleteDateTo
      )
    ) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "Unable to filter the report data. Please limit the date range to 3 months."
        })
      });
      navigate(financeOrderListUrl());
    }
  }, [
    params.createdFrom,
    params.createdTo,
    params.orderCompleteDateFrom,
    params.orderCompleteDateTo
  ]);

  const [BulkUpdateSettlementStatus] = useBulkUpdateSettlementMutation({
    onCompleted: event => {
      const { settlementStatusBulkUpdate } = event;
      const { payoutErrors } = settlementStatusBulkUpdate;
      refetchPayoutList();
      resetSelected();
      closeModal();
      setDownloadStatus("default");

      if (payoutErrors.length !== 0) {
        notify({
          status: "error",
          text: payoutErrors[0].message
        });
      }
    }
  });

  const [UpdateOpenOrders] = useUpdateOpenOrdersMutation({
    onCompleted: event => {
      const { payoutStoreOrders } = event;
      const { payoutErrors } = payoutStoreOrders;
      refetchPayoutList();
      if (payoutErrors.length !== 0) {
        notify({
          status: "error",
          text: payoutErrors[0].message
        });
        setDownloadStatus("default");
        closeModal();
      } else {
        switch (payoutStoreOrders.payoutStoreOrders.settlementStatus) {
          case SettlementStatusEnum.FOR_PAYMENT:
            onPayoutFinanceAdjust(
              payoutStoreOrders.payoutStoreOrders.settlementStatus,
              "1"
            );
            closeModal();
            break;
          case SettlementStatusEnum.EXCLUDED:
            onPayoutFinanceAdjust(
              payoutStoreOrders.payoutStoreOrders.settlementStatus,
              "2"
            );
            closeModal();
            break;

          default:
            break;
        }
      }
    },
    onError: () => {
      setDownloadStatus("default");
      closeModal();
    }
  });

  const handleUpdateOpenOrder = (formData: AdjustDialogForm) => {
    const {
      deliveryFeeAdjustment,
      otherAdjustment,
      otherAdjustmentRemarks,
      paymentFeeAdjustment,
      penalties,
      settlementStatus
    } = formData;
    setDownloadStatus("loading");

    UpdateOpenOrders({
      variables: {
        id: params.id,
        input: {
          deliveryFeeAdjustment: parseFloat(deliveryFeeAdjustment),
          otherAdjustment: parseFloat(otherAdjustment),
          otherAdjustmentRemarks,
          paymentFeeAdjustment: parseFloat(paymentFeeAdjustment),
          penalties: parseFloat(penalties),
          settlementStatus
        }
      }
    });
  };

  const {
    data: payoutBatchTask,
    refetch: refetchPayoutBatchTask
  } = usePayoutBatchTaskQuery({
    displayLoader: true,
    // will only trigger query if generatePayoutBatch returns a task_id
    skip: batchTask === "" || batchTask === null || batchTask === undefined,
    variables: {
      id: batchTask
    }
  });

  const [GeneraPayoutBatch] = useGeneratePayoutBatchMutation({
    onCompleted: event => {
      const { generatePayoutBatch } = event;
      const { batchErrors } = generatePayoutBatch;

      if (batchErrors.length !== 0) {
        if (batchErrors[0].field === "credit_date") {
          notify({
            status: "error",
            text: "Settlement date must be greater than start and end date"
          });
        } else {
          notify({
            status: "error",
            text: batchErrors[0].message
          });
        }
      } else {
        setBatchTask(generatePayoutBatch.task_id);
        setDownloadStatus("loading");
        notify({
          status: "success",
          text: "Generating batch...."
        });
      }
    }
  });

  const cancelGenerateBatch = () => {
    closeModal();
    refetchPayoutList();
    setBatchTask(null);
    setDownloadStatus("default");
  };

  useEffect(() => {
    // validates if batch generation is successful or fail
    if (
      payoutBatchTask?.payoutBatchTask !== undefined ||
      payoutBatchTask?.payoutBatchTask !== null
    ) {
      if (
        payoutBatchTask?.payoutBatchTask?.status ===
        PayoutBatchTaskStatus.SUCCESS
      ) {
        notify({
          status: "success",
          text: "Successfully generated batch...."
        });
        setBatchTask(null);
        setDownloadStatus("success");
        closeModal();
        navigate("/payout/batch");
      } else if (
        payoutBatchTask?.payoutBatchTask?.status ===
        PayoutBatchTaskStatus.FAILED
      ) {
        notify({
          status: "error",
          text: intl.formatMessage({
            defaultMessage: "Generating batch failed"
          })
        });
        setBatchTask(null);
        setDownloadStatus("error");
        closeModal();
      } else {
        refetchPayoutBatchTask();
      }
    }
  }, [payoutBatchTask]);

  const { data: payoutFinanceAdjustments } = usePayoutFinanceAdjustment({
    displayLoader: true,
    skip: params.action !== "showAdjustmentDialog",
    variables: {
      store_id: params.id
    }
  });

  const onPayoutFinanceAdjust = (
    status: SettlementStatusEnum,
    activeTab: string
  ) => {
    navigate(
      financeOrderListUrl({
        ...params,
        action: undefined,
        activeTab,
        payoutStatus: status
      })
    );

    setDownloadStatus("default");
  };

  return (
    <div>
      <FinanceOrderListPage
        activeTab={params.activeTab}
        currencySymbol={currencySymbol}
        currentTab={currentTab}
        download={handleDownloadDialog}
        expressForPayment={handleExpressPaymentDialog}
        filterOpts={filterOpts}
        initialSearch={params.query || ""}
        onFilterChange={filter => changeFilters(filter, true)}
        onAll={resetFilters}
        onTabChange={handleTabChange}
        tabs={tabs.map(tab => tab.name)}
        isChecked={isSelected}
        selected={listElements.length}
        toggle={toggle}
        toggleAll={toggleAll}
        orders={maybe(() =>
          PayoutOrders.storeOrdersPayout.edges.map(edge => edge.node)
        )}
        toolbar={createToolbar(openModal, params.payoutStatus)}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onUpdateListSettings={updateListSettings}
        pageInfo={pageInfo}
        settings={settings}
        brands={undefined}
        onAdd={function(): void {
          throw new Error("Function not implemented.");
        }}
        disabled={loading}
        onRowClick={id => () => openModal("showAdjustmentDialog", { id })}
        onTabDelete={function(): void {
          throw new Error("Function not implemented.");
        }}
        onTabSave={function(): void {
          throw new Error("Function not implemented.");
        }}
        onSearchChange={handleSearch}
        params={params}
        customAction={{
          className: classes.btnGenerateBatch,
          disabled: generateBatchDisabled,
          displayOn: params.payoutStatus === "FOR_PAYMENT",
          label: {
            className: classes.whiteColorLabel,
            value: "Generate Batch"
          },
          onClick: () => openModal("onClickGenerateBatch")
        }}
      />

      <ActionDialog
        actionBlocking={true}
        open={params.action === "download"}
        onClose={handleCancelDownload}
        disabled={downloadStatus === "loading"}
        confirmButtonState={downloadStatus}
        onConfirm={handleConfirmDownload}
        variant="default"
        title={intl.formatMessage(
          {
            defaultMessage: "Download {title}",
            description: "dialog title"
          },
          { title: reportStructure.title }
        )}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="You are about to download {title} with {total} rows. Do you want to continue?"
            values={{
              title: reportStructure.title,
              total: PayoutOrders?.storeOrdersPayout?.totalCount
            }}
          />
        </DialogContentText>
      </ActionDialog>

      <FinanceAdjustDialog
        buttonState={downloadStatus}
        payoutFinanceAdjustments={
          payoutFinanceAdjustments?.payoutFinanceAdjustment
        }
        open={params.action === "showAdjustmentDialog"}
        updateOpenOrders={handleUpdateOpenOrder}
        onClose={closeModal}
      />

      <GenerateBatchDialog
        open={params.action === "onClickGenerateBatch"}
        onGenerateBatch={data => {
          GeneraPayoutBatch({
            variables: {
              credit_date: data.creditDate,
              end_date: data.endDate,
              mall_id: params.mall[0],
              start_date: data.startDate
            }
          });
        }}
        onClose={cancelGenerateBatch}
        buttonState={downloadStatus}
      />

      <ActionDialog
        actionBlocking={true}
        buttonsAlignment="center"
        closeButtonLabel="back"
        disabled={downloadStatus === "loading"}
        open={params.action === "for-payment"}
        onClose={closeModal}
        confirmButtonState={downloadStatus}
        confirmKeepLabel={true}
        onConfirm={() => {
          handleBulkUpdate(
            SettlementStatusEnum.FOR_PAYMENT,
            listElements,
            BulkUpdateSettlementStatus
          );
          setDownloadStatus("loading");
        }}
        title=" "
      >
        <DialogContentText style={{ textAlign: "center" }}>
          <FormattedMessage
            defaultMessage="Are you sure you want to move the selected batch{es} for payment?"
            values={{
              es: listElements?.length > 1 ? "es" : ""
            }}
          />
        </DialogContentText>
      </ActionDialog>

      <ActionDialog
        actionBlocking={true}
        buttonsAlignment="center"
        closeButtonLabel="back"
        disabled={downloadStatus === "loading"}
        open={params.action === "for-payment-express"}
        onClose={closeModal}
        confirmButtonState={downloadStatus}
        confirmKeepLabel={true}
        onConfirm={() => {
          handleConfirmExpressPayment();
          setDownloadStatus("loading");
        }}
        title="Express For Payment"
      >
        <DialogContentText style={{ textAlign: "center" }}>
          <FormattedMessage
            defaultMessage="Are you sure you want to move the {total} orders to 'For Payment'?"
            values={{
              title: reportStructure.title,
              total: PayoutOrders?.storeOrdersPayout?.totalCount
            }}
          />
        </DialogContentText>
      </ActionDialog>

      <ActionDialog
        actionBlocking={true}
        buttonsAlignment="center"
        closeButtonLabel="back"
        disabled={downloadStatus === "loading"}
        open={params.action === "exclude"}
        onClose={closeModal}
        confirmButtonState={downloadStatus}
        confirmKeepLabel={true}
        onConfirm={() => {
          handleBulkUpdate(
            SettlementStatusEnum.EXCLUDED,
            listElements,
            BulkUpdateSettlementStatus
          );
          setDownloadStatus("loading");
        }}
        title=" "
      >
        <DialogContentText style={{ textAlign: "center" }}>
          <FormattedMessage
            defaultMessage="Are you sure you want to exclude the selected batch{es}?"
            values={{
              es: listElements?.length > 1 ? "es" : ""
            }}
          />
        </DialogContentText>
      </ActionDialog>

      <ActionDialog
        actionBlocking={true}
        buttonsAlignment="center"
        closeButtonLabel="back"
        disabled={downloadStatus === "loading"}
        open={params.action === "return"}
        onClose={closeModal}
        confirmButtonState={downloadStatus}
        confirmKeepLabel={true}
        onConfirm={() => {
          handleBulkUpdate(
            SettlementStatusEnum.REOPENED,
            listElements,
            BulkUpdateSettlementStatus
          );
          setDownloadStatus("loading");
        }}
        title=" "
      >
        <DialogContentText style={{ textAlign: "center" }}>
          <FormattedMessage
            defaultMessage="Are you sure you want to return the selected batch{es} as open orders?"
            values={{
              es: listElements?.length > 1 ? "es" : ""
            }}
          />
        </DialogContentText>
      </ActionDialog>
    </div>
  );
};

export default FinanceOrderList;
